import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Input, message, Modal } from 'antd';
import styled from 'styled-components';
import { AxiosResponse } from 'axios';
import CurrencyInputPrecision from '../../Promotion/InputPercentDollar/CurrencyInputPrecision';
import accountApis from '../../../../services/accountApis';
import { useParams } from 'react-router-dom';

interface Props {
    onSuccess?: () => void;
}

export interface TopUpAmountModalRef {
  open: () => void;
  close: () => void;
}

const TopUpAmountModal = forwardRef<TopUpAmountModalRef, Props>(({onSuccess}, ref) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { shopId } = useParams();

  const onOpen = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const response: AxiosResponse<{ data: any }> = await accountApis.createTopUpAmount(shopId ?? '', {
        amount: values.amount,
        note: values.note,
      });
      if (response.data.data) {
        message.success('Top up amount created successfully');

        onClose();
        onSuccess?.();
      }
    } catch (error) {
      message.error('Failed to create top up amount');
    } finally {
      setLoading(false);
    }
  };

  const onDone = () => {
    form.submit();
  };

  return (
    <Modal open={open} title={null} footer={null} width={511} centered destroyOnClose={false} closable={false}>
      <ContainerStyled>
        <TitleStyled>Top Up Amount</TitleStyled>
        <Form form={form} onFinish={onFinish} layout='vertical'>
          <Form.Item name='amount' label='Amount' rules={[{ required: true, message: 'Please enter amount' }]}>
            <CurrencyInputPrecision decimalLength={4} size='small'/>
          </Form.Item>
          <Form.Item name='note' label='Note'>
            <Input.TextArea rows={4} placeholder='Please enter...' />
          </Form.Item>
        </Form>
        <ButtonsStyled>
          <button type='button' className='btn-cancel' onClick={onClose}>
            Close
          </button>
          <button type='button' className='btn-submit' onClick={onDone} disabled={loading}>
            Done
          </button>
        </ButtonsStyled>
      </ContainerStyled>
    </Modal>
  );
});

export default TopUpAmountModal;

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .ant-form-item {
    margin-bottom: 8px;
  }
`;

const TitleStyled = styled.div`
  align-self: stretch;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const ButtonsStyled = styled.div`
  display: flex;
  gap: 16px;

  button {
    cursor: pointer;
    display: flex;
    height: 48px;
    padding: 16px 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 4px;

    text-align: center;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    border: none;
    outline: none;
    transition: all 0.3s ease;
    &.btn-cancel {
      background: #e3e9ed;
      color: #566a7f;
    }
    &.btn-submit {
      color: #fff;
      background: #ff8890;
    }
    &:hover {
      opacity: 0.8;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
